import { useState, useEffect, useCallback } from 'react';
import { OptionType, CreateOptionTypeRequest } from '../../types/optionTypes';
import { optionTypesService } from '../../services/optionTypesService';
import { useToast } from '../ui/useToast';

export const useOptionManagement = () => {
  const [optionTypes, setOptionTypes] = useState<OptionType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { showError, showSuccess } = useToast();

  // Data yükleme
  const loadOptionTypes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await optionTypesService.getAllOptionTypes();
      setOptionTypes(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Seçenek türleri yüklenemedi';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  // Initial load
  useEffect(() => {
    loadOptionTypes();
  }, [loadOptionTypes]);

  // Create option type
  const createOptionType = useCallback(
    async (data: CreateOptionTypeRequest) => {
      try {
        // is_active default olarak true olacak
        const createData = { ...data, is_active: data.is_active ?? true };
        await optionTypesService.createOptionType(createData);
        showSuccess('Seçenek türü başarıyla eklendi');
        await loadOptionTypes();
        return true;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'İşlem başarısız';
        showError(errorMessage);
        return false;
      }
    },
    [showError, showSuccess, loadOptionTypes]
  );

  // Update option type
  const updateOptionType = useCallback(
    async (id: number, data: Partial<OptionType>) => {
      try {
        await optionTypesService.updateOptionType(id, data);
        showSuccess('Seçenek türü başarıyla güncellendi');
        await loadOptionTypes();
        return true;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'İşlem başarısız';
        showError(errorMessage);
        return false;
      }
    },
    [showError, showSuccess, loadOptionTypes]
  );

  // Delete option type
  const deleteOptionType = useCallback(
    async (id: number) => {
      try {
        await optionTypesService.deleteOptionType(id);
        showSuccess('Seçenek türü başarıyla silindi');
        await loadOptionTypes();
        return true;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Silme işlemi başarısız';
        showError(errorMessage);
        return false;
      }
    },
    [showError, showSuccess, loadOptionTypes]
  );

  // Toggle active status
  const toggleActive = useCallback(
    async (option: OptionType) => {
      try {
        await optionTypesService.updateOptionType(option.id, {
          is_active: !option.is_active,
        });
        showSuccess(`Seçenek türü ${!option.is_active ? 'aktif edildi' : 'pasif edildi'}`);
        await loadOptionTypes();
        return true;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Durum güncellenemedi';
        showError(errorMessage);
        return false;
      }
    },
    [showError, showSuccess, loadOptionTypes]
  );

  return {
    // State
    optionTypes,
    loading,
    error,

    // Actions
    loadOptionTypes,
    createOptionType,
    updateOptionType,
    deleteOptionType,
    toggleActive,
  };
};
